/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "either", depth: 25 }] */
// eslint-disable-next-line camelcase
import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal,
} from 'flowbite-react';
import { BarWave } from 'react-cssfx-loading';
import parse from 'html-react-parser';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch, faEdit, faTrash, faFileAlt, faChartBar, faCalendarAlt, faEye, faClock, faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../../components/Pagination/Pagination';
import { useDeleteArticleMutation, useGetArticlesQuery } from '../../../../redux/apiSlice';

const PageSize = 10;
function PublishedArticles() {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [currenArticletId, setCurrentArticleId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [deletionStatus, setDeletionStatus] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const { data: articles, error, refetch } = useGetArticlesQuery();

  const navigate = useNavigate();

  useEffect(() => {
    if (articles) {
      setFilteredArticles(articles);
    }
  }, [articles]);

  useEffect(() => {
    if (articles) {
      refetch();
      const query = searchQuery.toLowerCase() || '';
      setFilteredArticles(
        articles.filter((article) => article.post_title.toLowerCase().includes(query)
          || article.name.toLowerCase().includes(query)
          || article.post_type.toLowerCase().includes(query)
          || article.post_content.toLowerCase().includes(query)
          || article.post_date.toLowerCase().includes(query)),
      );
    }
  }, [searchQuery, articles, refetch]);

  const [deleteArticleMutation] = useDeleteArticleMutation();

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDeleteArticle = async (articleId) => {
    setCurrentArticleId(articleId);
    setOpenDeleteModal(true);
  };

  const confirmDeleteArticle = async () => {
    try {
      await deleteArticleMutation(currenArticletId);
      setSuccessMsg('Article deleted Successfully.');
      setTimeout(() => {
        setSuccessMsg('');
      }, 2000);
      setDeletionStatus(true);
      setOpenDeleteModal(false);
    } catch (error) {
      console.log('error deleting article', error);
    }
  };

  useEffect(() => {
    if (deletionStatus) {
      refetch();
      setDeletionStatus(false);
    }
  }, [deletionStatus, refetch]);

  if (error) {
    console.error('Error fetching articles', error);
  }

  const paginatedLogs = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return filteredArticles.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredArticles]);

  // Calculate statistics
  const totalArticles = articles?.length || 0;
  const publishedArticles = articles?.filter((article) => article.post_status === 'published')?.length || 0;
  const draftArticles = articles?.filter((article) => article.post_status === 'draft')?.length || 0;
  const recentArticles = articles?.slice(0, 5) || [];
  const thisMonthArticles = articles?.filter((article) => {
    const articleDate = new Date(article.post_date);
    const now = new Date();
    return (
      articleDate.getMonth() === now.getMonth()
      && articleDate.getFullYear() === now.getFullYear()
    );
  })?.length || 0;

  // Find most viewed article (assuming view count is available, otherwise use most recent)
  const mostViewedArticle = articles ? [...articles].sort((a, b) => {
    // If view count is available, sort by that, otherwise by date
    const aViews = a.view_count || 0;
    const bViews = b.view_count || 0;
    if (aViews !== bViews) {
      return bViews - aViews;
    }
    return new Date(b.post_date) - new Date(a.post_date);
  })[0] : null;

  if (!articles) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <BarWave color="#6366f1" height="2rem" width="8rem" />
          <p className="mt-4 text-gray-300">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <FontAwesomeIcon icon={faFileAlt} className="text-white text-lg" />
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-white">Article Management</h1>
                    <p className="text-gray-300 text-sm">Manage safety articles and educational content</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/private/articles-form')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                  <span className="font-medium">Create Article</span>
                </button>
              </div>
            </div>

            {/* Search Section */}
            <div className="px-6 py-4 bg-gray-750 border-b border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                    />
                  </div>
                </div>
                <div className="ml-4 text-sm text-gray-300">
                  {filteredArticles.length}
                  {' '}
                  article
                  {filteredArticles.length !== 1 ? 's' : ''}
                  {' '}
                  found
                </div>
              </div>
            </div>

            {/* Success Message */}
            {successMsg && (
              <div className="mx-6 mt-4 bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg">
                <p className="text-center">{successMsg}</p>
              </div>
            )}

            {/* Table Section */}
            {filteredArticles && filteredArticles.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-750">
                    <tr className="border-b border-gray-600">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Author
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Content Preview
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {paginatedLogs.map(({
                      id, postTitle, name, postType, postContent, postDate,
                    }, index) => (
                      <tr key={id} className={`hover:bg-gray-750 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-white">
                            {parse(postTitle)}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-white">{name}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
                            {postType}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-300 max-w-xs truncate">
                            {postContent.replace(/<[^>]*>/g, '').substring(0, 100)}
                            ...
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-300">
                            {new Date(postDate).toLocaleDateString('en-UK', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/private/editArticle/${id}`)}
                              className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-all duration-200"
                              title="Edit Article"
                            >
                              <FontAwesomeIcon icon={faEdit} className="text-sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteArticle(id)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-all duration-200"
                              title="Delete Article"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon={faFileAlt} className="text-2xl text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">No articles found</h3>
                <p className="text-gray-400 mb-6">Get started by creating your first article</p>
                <button
                  type="button"
                  onClick={() => navigate('/private/articles-form')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Create First Article
                </button>
              </div>
            )}

            {/* Pagination */}
            {filteredArticles && filteredArticles.length > 0 && (
              <div className="px-6 py-4 bg-gray-750 border-t border-gray-600">
                <Pagination
                  itemsDisplaying={paginatedLogs.length}
                  itemsPerPage={PageSize}
                  items={filteredArticles}
                  handleNext={() => setCurrentPage(currentPage + 1)}
                  handlePrevious={() => setCurrentPage(currentPage - 1)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Article Statistics */}
        <div className="w-80 space-y-6">
          {/* Statistics Overview */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-600 rounded-lg">
                <FontAwesomeIcon icon={faChartBar} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Article Statistics</h3>
                <p className="text-gray-300 text-sm">Overview of your content</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFileAlt} className="text-blue-400" />
                  <span className="text-gray-300">Total Articles</span>
                </div>
                <span className="text-2xl font-bold text-white">{totalArticles}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-400" />
                  <span className="text-gray-300">Published</span>
                </div>
                <span className="text-2xl font-bold text-green-400">{publishedArticles}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faClock} className="text-yellow-400" />
                  <span className="text-gray-300">Drafts</span>
                </div>
                <span className="text-2xl font-bold text-yellow-400">{draftArticles}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-400" />
                  <span className="text-gray-300">This Month</span>
                </div>
                <span className="text-2xl font-bold text-purple-400">{thisMonthArticles}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faEye} className="text-orange-400" />
                  <span className="text-gray-300">Most Viewed</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-white truncate max-w-24">
                    {mostViewedArticle?.post_title || 'N/A'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {mostViewedArticle?.view_count || 0}
                    {' '}
                    views
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Articles */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-600 rounded-lg">
                <FontAwesomeIcon icon={faEye} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Recent Articles</h3>
                <p className="text-gray-300 text-sm">Latest published content</p>
              </div>
            </div>

            <div className="space-y-3">
              {recentArticles.map((article) => (
                <div key={article.id} className="p-3 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-white truncate">
                        {article.post_title}
                      </h4>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(article.post_date).toLocaleDateString('en-UK', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-600 rounded-lg">
                <FontAwesomeIcon icon={faPlus} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Quick Actions</h3>
                <p className="text-gray-300 text-sm">Common tasks</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/private/articles-form')}
                className="w-full flex items-center space-x-3 p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-white" />
                <span className="text-white font-medium">Create New Article</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/private/articles')}
                className="w-full flex items-center space-x-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faFileAlt} className="text-gray-300" />
                <span className="text-gray-300 font-medium">View All Articles</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} popup>
        <Modal.Header className="bg-gray-700 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">Delete Article</h3>
        </Modal.Header>
        <Modal.Body className="bg-gray-800">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faTrash} className="text-2xl text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Confirm Deletion</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to delete this article? This action cannot be undone.</p>
            <div className="flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={() => setOpenDeleteModal(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteArticle}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Article
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default PublishedArticles;
